import { inject, InjectionToken, Provider, Type } from '@angular/core';
import { Party } from '@jms/model';
import type { CatalogueCode, CodeUoM, CustomFieldMappingEnriched, CustomFieldParent, PurchaseOrderStatus, StageItemDetail, Task } from '@jms/model';
import type { Command } from '@workbuddy/ui/model';
import { ActivityHost, OVERRIDE_ACTIVITY_SERVICE } from '@workbuddy/ui/modules/activities';
import type { ActivityService } from '@workbuddy/ui/modules/activities';
import type { TaskPermission } from '@workbuddy/ui/modules/tasks/src/models';
import { Observable, of } from 'rxjs';
import type { ItemEditorField, ItemFieldRuleFacts, JobItemBoardParams } from '../../../models/job.item.model';
import type { ItemActivityRights } from './item.detail.activity.context';

/** What the SOR Details page shows per host; the office shows everything, a subcontractor sees only their own money and work. */
export interface ItemDetailHostVisibility {
  readonly statusPicker: boolean;
  readonly sellPrice: boolean;
  readonly buyPrice: boolean;
  readonly extPrices: boolean;
  readonly margin: boolean;
  readonly financial: boolean;
  readonly customFields: boolean;
  readonly approveDecline: boolean;
  readonly workAlreadyCompleted: boolean;
  /** The line's activity feed; the client has no item activity screen. */
  readonly activities: boolean;
  /** The Completed By row; the client task carries no completer, so it would read Not Set for every task. */
  readonly completedBy: boolean;
  /** Per-photo evidence badges; they recompute from the evidence exception, which the client task does not carry. */
  readonly evidenceBuckets: boolean;
}

export interface ItemDetailRepriceArgs {
  readonly field: 'buyPrice' | 'sellPrice';
  readonly detail: StageItemDetail;
  readonly codeUoM: CodeUoM;
  readonly isCustom: boolean;
  readonly isReadonly: boolean;
}

/** How a read-only host's rail reads its task: the preview operation, through a read that holds the line's own rules. */
export type ItemDetailTaskRailRead = {
  readonly operation: 'preview';
  readonly loadTask: (taskId: string) => Promise<Task | null>;
};

/** Inputs every task rail component takes; a type, not an interface, so it meets `ngComponentOutletInputs`' index signature. */
export type ItemDetailTaskRailInputs = {
  readonly taskId: string;
  readonly closed: () => void;
  readonly changed: () => void;
  /** Null opens the task for edit through the default read. */
  readonly read: ItemDetailTaskRailRead | null;
};

/** A task rail component; the page asks it before swapping the task or closing, so an unsaved task write is not dropped. */
export interface ItemDetailTaskRail {
  canDeactivate(): Promise<boolean>;
}

/** Ids the opener holds; the loaded line's stage may be thin. */
export interface ItemDetailHostScope {
  readonly jobId?: string;
  readonly stageId?: string;
}

/** Answers a request the server refused as an outcome or an error; null when it was requested. */
export type ItemDetailRequestRefusal = { readonly reason?: string } | null;

/** What differs on SOR Details between the office and the Contractor Portal; each app provides its own, so the page reads no right, route or domain (WBS-12033). */
export interface ItemDetailHost {
  /** The party the server judges this user's writes as. */
  readonly party: Party;
  readonly canManageJob: boolean;
  readonly taskPermission: TaskPermission;
  readonly visible: ItemDetailHostVisibility;
  readonly buyPriceLabel: string;
  /** The activity service the feed reads through, or null to inherit the parent's. */
  readonly activityService: (() => ActivityService) | null;
  readonly activityHost: ActivityHost;
  fieldReadonly(detail: StageItemDetail | null, facts: ItemFieldRuleFacts): Record<ItemEditorField, boolean>;
  /** The reason to show for a write the server refused. */
  refusal(error: unknown): string | undefined;
  /** Whether this user may send this line for review once it is a Draft: their rights and the line's author. */
  canRequest(detail: StageItemDetail | null): boolean;
  activityRights(stageOpenForContractor: boolean): ItemActivityRights;
  catalogueCode(name: string, fields: readonly (keyof CatalogueCode)[]): Observable<Partial<CatalogueCode> | null>;
  purchaseOrderStatus(id: string): Observable<PurchaseOrderStatus | null>;
  /** The pricebook figure for the line, or null where this host cannot read the pricebook. */
  reprice(args: ItemDetailRepriceArgs): Promise<number | null>;
  customFieldMappings(parent: CustomFieldParent): Promise<CustomFieldMappingEnriched[]>;
  /** Header ⋮ commands; runs in the page's injection context. `onStatusChanged` re-reads the page after a status move that leaves it open. */
  menuCommands(detail: StageItemDetail, scope: ItemDetailHostScope, params: JobItemBoardParams | null, onDeleted: () => void, onStatusChanged: () => void): Command[];
  requestItem(detail: StageItemDetail, scope: ItemDetailHostScope): Promise<ItemDetailRequestRefusal>;
  /** The right-column component a task click opens, taking {@link ItemDetailTaskRailInputs}; null opens nothing. */
  readonly taskRail: (() => Promise<Type<ItemDetailTaskRail>>) | null;
  /** The rail's read for the line with this id; null keeps the edit operation and its default read. */
  readonly taskRailRead: ((itemId: string) => ItemDetailTaskRailRead) | null;
  /** No write on this page, so it skips the lookups only a write uses. */
  readonly readOnly: boolean;
  /** The page may open from its typed address (no board params) and closes itself when the server refuses or finds no line (WBS-12280). */
  readonly opensByAddress: boolean;
}

export const ITEM_DETAIL_HOST = new InjectionToken<ItemDetailHost>('work.job.item.ITEM_DETAIL_HOST');

/** Every field locked; for a host that never writes a line. */
export const READ_ONLY_FIELDS: Record<ItemEditorField, boolean> = { location: true, note: true, description: true, longDescription: true, quantity: true, codeUoM: true, buyPrice: true, sellPrice: true, status: true, codeInvoicingStatus: true, suppliedBySubcontractor: true, payable: true, lineCustomFields: true };

/** An app that registers no host opens the page read-only rather than borrowing another app's rights and routes. */
const READ_ONLY_ITEM_DETAIL_HOST: ItemDetailHost = {
  party: Party.Office,
  canManageJob: false,
  taskPermission: { addTask: false, editTask: false },
  visible: { statusPicker: false, sellPrice: false, buyPrice: true, extPrices: false, margin: false, financial: false, customFields: false, approveDecline: false, workAlreadyCompleted: false, activities: true, completedBy: true, evidenceBuckets: true },
  buyPriceLabel: 'Buy Price',
  activityService: null,
  activityHost: ActivityHost.Tenant,
  fieldReadonly: () => READ_ONLY_FIELDS,
  canRequest: () => false,
  refusal: () => undefined,
  activityRights: () => ({ manageActivity: false, manageOtherUserActivity: false }),
  catalogueCode: () => of(null),
  purchaseOrderStatus: () => of(null),
  reprice: () => Promise.resolve(null),
  customFieldMappings: () => Promise.resolve([]),
  menuCommands: () => [],
  requestItem: () => Promise.resolve({}),
  taskRail: null,
  taskRailRead: null,
  readOnly: true,
  opensByAddress: false,
};

/** The app's host, or read-only where the app registered none. */
export function injectItemDetailHost(): ItemDetailHost {

  return inject(ITEM_DETAIL_HOST, { optional: true }) ?? READ_ONLY_ITEM_DETAIL_HOST;
}

/** At page level, not root: a root override would also replace the client portal's route-level feed service. */
export const itemDetailActivityServiceProvider: Provider = {
  provide: OVERRIDE_ACTIVITY_SERVICE,
  useFactory: () => injectItemDetailHost().activityService?.() ?? inject(OVERRIDE_ACTIVITY_SERVICE, { optional: true, skipSelf: true }),
};
